/** Editor de texto enriquecido 100% Polaris, estilo Shopify Admin. */

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import UnderlineExtension from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { useCallback, useState } from 'react';
import {
  Button,
  Select,
  Popover,
  ActionList,
  InlineStack,
  Box,
  Text,
  InlineError,
  BlockStack,
  Modal,
  TextField,
} from '@/lib/polaris';
import {
  ImageIcon,
  LinkIcon,
  TextBoldIcon,
  TextItalicIcon,
  TextUnderlineIcon,
  CodeIcon,
  ListBulletedIcon,
  TextColorIcon,
} from '@/lib/polaris-icons';

interface PolarisRichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
}

const TEXT_STYLES = [
  { value: 'paragraph', label: 'Párrafo' },
  { value: 'heading1', label: 'Encabezado 1' },
  { value: 'heading2', label: 'Encabezado 2' },
  { value: 'heading3', label: 'Encabezado 3' },
  { value: 'bulletList', label: 'Lista con viñetas' },
  { value: 'orderedList', label: 'Lista numerada' },
];

const COLORS = [
  '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC',
  '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF', '#980000', '#FF0000',
  '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF',
  '#9900FF', '#FF00FF', '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC',
  '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
];

export function PolarisRichTextEditor({ 
  value, 
  onChange, 
  placeholder, 
  error 
}: PolarisRichTextEditorProps) {
  const [colorPopoverActive, setColorPopoverActive] = useState(false);
  const [alignPopoverActive, setAlignPopoverActive] = useState(false);
  const [linkModalOpen, setLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [htmlModalOpen, setHtmlModalOpen] = useState(false);
  const [htmlContent, setHtmlContent] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      UnderlineExtension,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: false,
      }),
      Image.configure({}),
      TextStyle.configure({}),
      Color.configure({}),
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'sf-rich-text-editor-content',
        'data-placeholder': placeholder || 'Escribe aquí...',
      },
    },
  });

  const openLinkModal = useCallback(() => {
    if (!editor) return;
    const previousUrl = editor.getAttributes('link').href || '';
    setLinkUrl(previousUrl);
    setLinkModalOpen(true);
  }, [editor]);

  const applyLink = useCallback(() => {
    if (!editor) return;
    setLinkModalOpen(false);
    if (linkUrl.trim() === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl.trim() }).run();
    setLinkUrl('');
  }, [editor, linkUrl]);

  const openImageModal = useCallback(() => {
    setImageUrl('');
    setImageModalOpen(true);
  }, []);

  const applyImage = useCallback(() => {
    if (!editor || !imageUrl.trim()) return;
    editor.chain().focus().setImage({ src: imageUrl.trim() }).run();
    setImageModalOpen(false);
    setImageUrl('');
  }, [editor, imageUrl]);

  const openHtmlModal = useCallback(() => {
    if (!editor) return;
    setHtmlContent(editor.getHTML());
    setHtmlModalOpen(true);
  }, [editor]);

  const applyHtml = useCallback(() => {
    if (!editor) return;
    editor.commands.setContent(htmlContent);
    setHtmlModalOpen(false);
  }, [editor, htmlContent]);

  const setTextStyle = (style: string) => {
    if (!editor) return;

    switch (style) {
      case 'heading1':
        editor.chain().focus().setHeading({ level: 1 }).run();
        break;
      case 'heading2':
        editor.chain().focus().setHeading({ level: 2 }).run();
        break;
      case 'heading3':
        editor.chain().focus().setHeading({ level: 3 }).run();
        break;
      case 'bulletList':
        editor.chain().focus().toggleBulletList().run();
        break;
      case 'orderedList':
        editor.chain().focus().toggleOrderedList().run();
        break;
      default:
        editor.chain().focus().setParagraph().run();
        break;
    }
  };

  const getCurrentTextStyle = () => {
    if (!editor) return 'paragraph';
    
    if (editor.isActive('heading', { level: 1 })) return 'heading1';
    if (editor.isActive('heading', { level: 2 })) return 'heading2';
    if (editor.isActive('heading', { level: 3 })) return 'heading3';
    if (editor.isActive('bulletList')) return 'bulletList';
    if (editor.isActive('orderedList')) return 'orderedList';
    return 'paragraph';
  };

  const setTextColor = (color: string) => {
    if (!editor) return;
    editor.chain().focus().setColor(color).run();
    setColorPopoverActive(false);
  };

  const setTextAlign = (align: 'left' | 'center' | 'right') => {
    if (!editor) return;
    editor.chain().focus().setTextAlign(align).run();
    setAlignPopoverActive(false);
  };

  if (!editor) {
    return null;
  }

  const currentTextStyle = getCurrentTextStyle();

  return (
    <Box
      borderWidth="025"
      borderColor="border"
      borderRadius="200"
      overflow="hidden"
      background="bg-surface"
    >
      <BlockStack gap="0">
        <Box
          padding="200"
          borderBlockEndWidth="025"
          borderColor="border-secondary"
          background="bg-surface-secondary"
        >
        <InlineStack gap="200" blockAlign="center" wrap>
          {/* Selector de estilo de texto */}
          <Box minWidth="140px">
            <Select
              label=""
              labelHidden
              options={TEXT_STYLES.map(style => ({
                label: style.label,
                value: style.value,
              }))}
              value={currentTextStyle}
              onChange={setTextStyle}
            />
          </Box>
          <Box width="1px" minHeight="20px" background="bg-border" />

          {/* Botones de formato */}
          <Button
            variant="tertiary"
            size="micro"
            icon={TextBoldIcon}
            onClick={() => editor.chain().focus().toggleBold().run()}
            accessibilityLabel="Negrita"
            pressed={editor.isActive('bold')}
          />
          <Button
            variant="tertiary"
            size="micro"
            icon={TextItalicIcon}
            onClick={() => editor.chain().focus().toggleItalic().run()}
            accessibilityLabel="Cursiva"
            pressed={editor.isActive('italic')}
          />
          <Button
            variant="tertiary"
            size="micro"
            icon={TextUnderlineIcon}
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            accessibilityLabel="Subrayado"
            pressed={editor.isActive('underline')}
          />
          <Box width="1px" minHeight="20px" background="bg-border" />

          {/* Selector de color */}
          <Popover
            active={colorPopoverActive}
            activator={
              <Button
                variant="tertiary"
                size="micro"
                icon={TextColorIcon}
                onClick={() => setColorPopoverActive(!colorPopoverActive)}
                accessibilityLabel="Color de texto"
              />
            }
            onClose={() => setColorPopoverActive(false)}
          >
            <Box padding="300" minWidth="280px">
              <Text variant="bodySm" fontWeight="semibold" as="p" alignment="start">
                Color de texto
              </Text>
              <Box paddingBlockStart="200">
                <Box
                  as="div"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(10, 1fr)',
                    gap: 'var(--p-space-100)',
                  }}
                >
                  {COLORS.map((color) => (
                    <Button
                      key={color}
                      variant="plain"
                      size="slim"
                      onClick={() => setTextColor(color)}
                      accessibilityLabel={`Color ${color}`}
                      style={{
                        width: 24,
                        height: 24,
                        minWidth: 24,
                        minHeight: 24,
                        padding: 0,
                        backgroundColor: color,
                        border: '1px solid var(--p-color-border, #e1e3e5)',
                        borderRadius: 'var(--p-border-radius-100, 4px)',
                      }}
                    />
                  ))}
                </Box>
              </Box>
            </Box>
          </Popover>
          <Box width="1px" minHeight="20px" background="bg-border" />

          {/* Alineación y listas */}
          <Popover
            active={alignPopoverActive}
            activator={
              <Button
                variant="tertiary"
                size="micro"
                icon={ListBulletedIcon}
                onClick={() => setAlignPopoverActive(!alignPopoverActive)}
                accessibilityLabel="Alineación y listas"
              />
            }
            onClose={() => setAlignPopoverActive(false)}
          >
            <ActionList
              items={[
                {
                  content: 'Alinear izquierda',
                  onAction: () => setTextAlign('left'),
                },
                {
                  content: 'Alinear centro',
                  onAction: () => setTextAlign('center'),
                },
                {
                  content: 'Alinear derecha',
                  onAction: () => setTextAlign('right'),
                },
                {
                  content: 'Lista con viñetas',
                  onAction: () => {
                    if (editor) {
                      editor.chain().focus().toggleBulletList().run();
                      setAlignPopoverActive(false);
                    }
                  },
                },
                {
                  content: 'Lista numerada',
                  onAction: () => {
                    if (editor) {
                      editor.chain().focus().toggleOrderedList().run();
                      setAlignPopoverActive(false);
                    }
                  },
                },
              ]}
            />
          </Popover>

          {/* Enlace */}
          <Button
            variant="tertiary"
            size="micro"
            icon={LinkIcon}
            pressed={editor.isActive('link')}
            onClick={openLinkModal}
            accessibilityLabel="Insertar enlace"
          />

          {/* Imagen */}
          <Button
            variant="tertiary"
            size="micro"
            icon={ImageIcon}
            onClick={openImageModal}
            accessibilityLabel="Insertar imagen"
          />
          <Box width="1px" minHeight="20px" background="bg-border" />

          {/* Vista código HTML */}
          <Button
            variant="tertiary"
            size="micro"
            icon={CodeIcon}
            onClick={openHtmlModal}
            accessibilityLabel="Editar HTML"
          />
        </InlineStack>
        </Box>

        {/* Área de contenido: estilos en .sf-rich-text-editor-content (polaris.css) */}
        <Box borderBlockStartWidth="025" borderColor="border-secondary" minHeight="12.5rem">
          <EditorContent editor={editor} />
        </Box>

        {error && (
          <Box padding="200" borderBlockStartWidth="025" borderColor="border-secondary">
            <InlineError message={error} fieldID="sf-rich-text-editor" />
          </Box>
        )}
      </BlockStack>

      {/* Modal: insertar/editar enlace (100% Polaris) */}
      <Modal
        open={linkModalOpen}
        onClose={() => { setLinkModalOpen(false); setLinkUrl(''); }}
        title="Insertar enlace"
        primaryAction={{ content: 'Aplicar', onAction: applyLink }}
        secondaryActions={[
          {
            content: 'Quitar enlace',
            destructive: true,
            onAction: () => {
              if (editor) {
                editor.chain().focus().extendMarkRange('link').unsetLink().run();
                setLinkModalOpen(false);
                setLinkUrl('');
              }
            },
          },
          { content: 'Cancelar', onAction: () => { setLinkModalOpen(false); setLinkUrl(''); } },
        ]}
      >
        <Modal.Section>
          <TextField
            label="URL"
            value={linkUrl}
            onChange={setLinkUrl}
            placeholder="https://"
            autoComplete="off"
          />
        </Modal.Section>
      </Modal>

      {/* Modal: insertar imagen (100% Polaris) */}
      <Modal
        open={imageModalOpen}
        onClose={() => { setImageModalOpen(false); setImageUrl(''); }}
        title="Insertar imagen"
        primaryAction={{ content: 'Insertar', onAction: applyImage }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => { setImageModalOpen(false); setImageUrl(''); } }]}
      >
        <Modal.Section>
          <TextField
            label="URL de la imagen"
            value={imageUrl}
            onChange={setImageUrl}
            placeholder="https://"
            autoComplete="off"
          />
        </Modal.Section>
      </Modal>

      {/* Modal: editar HTML (100% Polaris) */}
      <Modal
        open={htmlModalOpen}
        onClose={() => setHtmlModalOpen(false)}
        title="Editar HTML"
        size="large"
        primaryAction={{ content: 'Aplicar', onAction: applyHtml }}
        secondaryActions={[{ content: 'Cancelar', onAction: () => setHtmlModalOpen(false) }]}
      >
        <Modal.Section>
          <TextField
            label="HTML"
            value={htmlContent}
            onChange={setHtmlContent}
            multiline={10}
            autoComplete="off"
          />
        </Modal.Section>
      </Modal>
    </Box>
  );
}
